import { useQuery } from '@apollo/client';
import { ActionIcon, Group, Paper, Stack, Text, TextInput, ThemeIcon } from '@mantine/core';
import { productListDocument } from '../graphql/get-products';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { QueryMode, SortOrder } from '@/__generated__/graphql';
import { DataTable } from 'mantine-datatable';
import { IconCheck, IconCopy, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { modals } from '@mantine/modals';
import { ProductUpsertModal } from './ProductUpsertModal';
import { countProductsDocument } from '../graphql/count-products';
import { useState } from 'react';
import { ProductVariantsTable } from './ProductVariantsTable';

export const ProductFindModal = ({ liveId, query }: { liveId?: string; query?: string }) => {
  const [search, setSearch] = useInputState(query || '');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tokens = debouncedSearch
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 0);

  const { data, loading } = useQuery(productListDocument, {
    variables: {
      where: {
        AND: tokens.map((token) => ({
          OR: [
            { code: { contains: token, mode: QueryMode.Insensitive } },
            { name: { contains: token, mode: QueryMode.Insensitive } },
          ],
        })),
      },
      orderBy: { updatedAt: SortOrder.Desc },
      skip: (page - 1) * pageSize,
      take: pageSize,
    },
    skip: tokens.length === 0,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: count } = useQuery(countProductsDocument, {
    variables: {
      where: {
        AND: tokens.map((token) => ({
          OR: [
            { code: { contains: token, mode: QueryMode.Insensitive } },
            { name: { contains: token, mode: QueryMode.Insensitive } },
          ],
        })),
      },
    },
    skip: tokens.length === 0,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  return (
    <Stack>
      <Paper component={Stack} p="md" withBorder>
        <TextInput
          label="Kód / názov produktu"
          placeholder="Zadajte kód alebo názov produktu"
          value={search}
          onChange={setSearch}
          data-autofocus
        />
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        records={data?.products}
        fetching={loading}
        minHeight={180}
        totalRecords={count?.countProducts ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25]}
        onRecordsPerPageChange={setPageSize}
        columns={[
          {
            accessor: 'code',
            title: 'Kód',
          },
          {
            accessor: 'name',
            title: 'Názov',
            sortable: true,
            render: ({ name, description }) => (
              <Stack gap={0}>
                <Text inherit>{name}</Text>
                <Text inherit size="sm" c="dimmed">
                  {description}
                </Text>
              </Stack>
            ),
          },
          {
            accessor: 'price',
            title: 'Cena',
            render: ({ price }) => `${price} €`,
          },
          {
            accessor: 'active',
            title: 'Stav',
            render: ({ active }) =>
              active ? (
                <ThemeIcon size="sm" variant="subtle" color="teal">
                  <IconCheck size={16} />
                </ThemeIcon>
              ) : (
                <ThemeIcon size="sm" variant="subtle" color="red">
                  <IconX size={16} />
                </ThemeIcon>
              ),
          },
          {
            accessor: 'updatedAt',
            title: 'Dátum',
            render: ({ createdAt, updatedAt }) => (
              <Stack gap={0}>
                <Text inherit>{dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                {updatedAt > createdAt && (
                  <Text inherit c="dimmed" size="sm">
                    {dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'actions',
            title: '',
            render: (product) => (
              <Group>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="teal"
                  onClick={(e) => {
                    e.stopPropagation();

                    modals.closeAll();

                    const code = product.active
                      ? product.name.match(/\(([^)]+)\)$/)?.[1] || ''
                      : product.code;

                    modals.open({
                      title: `Vytvoriť nový produkt (skopírovaný)`,
                      size: '80%',
                      closeOnClickOutside: false,
                      children: (
                        <ProductUpsertModal
                          liveId={liveId}
                          defaultValues={{
                            canRemove: false,
                            code: code,
                            name: product.name,
                            description: product.description,
                            price: product.price,
                          }}
                        />
                      ),
                    });
                  }}
                >
                  <IconCopy size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        rowExpansion={{
          content: ({ record: { id } }) => <ProductVariantsTable productId={id} />,
        }}
      />
    </Stack>
  );
};
